import { useRef, useState } from 'react';
import { errorText } from '@/shared/api';
import { toast } from '@/shared/lib';
import { Field } from '@/shared/ui';
import { useUploadImageMutation } from '../api/guide';
import { addImages, captionImage, insertStep, linkImage, moveImage, moveStep, removeImage, removeStep, stepsUsing, updateStep } from '../model/edit';
import { imageUrl, type Guide } from '../model/steps';

const MAX_MB = 10;

// The Super Admin's editor for one guide: its name and steps on the left (type, reorder, add, delete, choose each step's
// picture), the picture gallery on the right (upload, caption, reorder, remove). Nothing is saved until "Save guide".
export function GuideEditor({ guide, onChange }: { guide: Guide; onChange: (g: Guide) => void }) {
  const [upload] = useUploadImageMutation();
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const n = guide.steps.length;

  const addFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true);
    const ids: string[] = [];
    try {
      for (const f of Array.from(files)) {
        if (f.size > MAX_MB * 1024 * 1024) { toast(`"${f.name}" is too big (${MAX_MB} MB at most).`, true); continue; }
        try { ids.push((await upload(f).unwrap()).id); } catch (e) { toast(`"${f.name}": ${errorText(e)}`, true); }
      }
      if (ids.length) {
        onChange(addImages(guide, ids));
        toast(`${ids.length} picture${ids.length > 1 ? 's' : ''} added. Choose which step shows ${ids.length > 1 ? 'each' : 'it'}, then Save guide.`);
      }
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="help-layout with-gallery editing">
      <div className="help-col">
        <div className="grid2">
          <Field label="Tab name">{(id) => <input id={id} value={guide.tab} maxLength={60} onChange={(e) => onChange({ ...guide, tab: e.target.value })} />}</Field>
          <Field label="Title">{(id) => <input id={id} value={guide.title} maxLength={80} onChange={(e) => onChange({ ...guide, title: e.target.value })} />}</Field>
        </div>
        <Field label="Introduction (optional)">{(id) => <input id={id} value={guide.intro} maxLength={400} onChange={(e) => onChange({ ...guide, intro: e.target.value })} />}</Field>
        <div className="grid2">
          <Field label="Download button text (optional)">{(id) => <input id={id} value={guide.link?.label ?? ''} maxLength={80} placeholder="Download"
            onChange={(e) => onChange({ ...guide, link: { url: guide.link?.url ?? '', label: e.target.value } })} />}</Field>
          <Field label="Download link (optional)" hint="Leave empty for no download button.">{(id) => <input id={id} type="url" value={guide.link?.url ?? ''} maxLength={500} placeholder="https://"
            onChange={(e) => onChange({ ...guide, link: { label: guide.link?.label ?? '', url: e.target.value } })} />}</Field>
        </div>

        <h4 className="edit-head">Steps</h4>
        <ol className="edit-steps">
          {guide.steps.map((st, i) => (
            <li key={st.id} className="edit-step">
              <span className="help-n" aria-hidden="true">{i + 1}</span>
              <div className="edit-body">
                <Field label={`Step ${i + 1}`}>{(id) => <input id={id} value={st.text} maxLength={300} placeholder="What to do" onChange={(e) => onChange(updateStep(guide, i, { text: e.target.value }))} />}</Field>
                <div className="grid2">
                  <Field label={`Note under step ${i + 1} (optional)`}>{(id) => <input id={id} value={st.detail ?? ''} maxLength={500} onChange={(e) => onChange(updateStep(guide, i, { detail: e.target.value }))} />}</Field>
                  <Field label={`Picture for step ${i + 1}`}>
                    {(id) => (
                      <select id={id} value={st.image ?? ''} onChange={(e) => onChange(linkImage(guide, i, e.target.value || undefined))}>
                        <option value="">No picture</option>
                        {guide.images.map((im, k) => <option key={im.id} value={im.id}>Picture {k + 1}{im.caption ? `: ${im.caption}` : ''}</option>)}
                      </select>
                    )}
                  </Field>
                </div>
                <div className="edit-actions">
                  <button type="button" className="btn ghost sm" aria-label={`Move step ${i + 1} up`} disabled={i === 0} onClick={() => onChange(moveStep(guide, i, i - 1))}>↑ Up</button>
                  <button type="button" className="btn ghost sm" aria-label={`Move step ${i + 1} down`} disabled={i === n - 1} onClick={() => onChange(moveStep(guide, i, i + 1))}>↓ Down</button>
                  <button type="button" className="btn ghost sm" aria-label={`Add a step above step ${i + 1}`} onClick={() => onChange(insertStep(guide, i))}>+ Step above</button>
                  <button type="button" className="btn ghost sm danger" aria-label={`Delete step ${i + 1}`} disabled={n === 1} onClick={() => onChange(removeStep(guide, i))}>Delete</button>
                </div>
              </div>
            </li>
          ))}
        </ol>
        <button type="button" className="btn ghost" onClick={() => onChange(insertStep(guide, n))}>+ Add a step at the end</button>
      </div>

      <aside className="help-gallery" aria-label="Pictures">
        <h4 className="edit-head">Pictures</h4>
        <p className="hint" style={{ marginTop: 0 }}>Upload screenshots, then choose under each step which picture it shows. Steps keep their picture when you add or move steps.</p>
        <button type="button" className="btn" disabled={busy} onClick={() => fileRef.current?.click()}>{busy ? 'Uploading…' : '↑ Upload pictures'}</button>
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" multiple hidden aria-label="Upload pictures" onChange={(e) => void addFiles(e.target.files)} />
        {guide.images.length === 0 ? <p className="hint">No pictures yet.</p> : (
          <ol className="edit-gallery">
            {guide.images.map((im, k) => {
              const used = stepsUsing(guide, im.id);
              return (
                <li key={im.id}>
                  <img src={imageUrl(im.id)} alt={`Picture ${k + 1}`} />
                  <div className="edit-body">
                    <div className="edit-pic-head"><b>Picture {k + 1}</b> <span className="hint">{used.length ? `shown at step ${used.join(', ')}` : 'not used by a step yet'}</span></div>
                    <Field label={`Caption for picture ${k + 1} (optional)`}>{(id) => <input id={id} value={im.caption} maxLength={200} onChange={(e) => onChange(captionImage(guide, k, e.target.value))} />}</Field>
                    <div className="edit-actions">
                      <button type="button" className="btn ghost sm" aria-label={`Move picture ${k + 1} up`} disabled={k === 0} onClick={() => onChange(moveImage(guide, k, k - 1))}>↑ Up</button>
                      <button type="button" className="btn ghost sm" aria-label={`Move picture ${k + 1} down`} disabled={k === guide.images.length - 1} onClick={() => onChange(moveImage(guide, k, k + 1))}>↓ Down</button>
                      <button type="button" className="btn ghost sm danger" aria-label={`Remove picture ${k + 1}`}
                        onClick={() => { onChange(removeImage(guide, k)); if (used.length) toast(`Picture removed; step ${used.join(', ')} no longer show${used.length > 1 ? '' : 's'} a picture.`); }}>Remove</button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </aside>
    </div>
  );
}
